/**
 * Checks 4 and 5 — caption length, and whether the hook survives the fold.
 *
 * Length reads `getPlatformSpec` (src/constants/platform-specs.ts), which
 * already aliases 'x' → 'twitter' and returns null for unknown platforms.
 */
import { platformDisplayName } from '../../../../shared/posting-readiness.ts';
import { type PlatformId, getPlatformSpec } from '../../../constants/platform-specs.ts';
import { trimCaptionToLimitFix } from '../fixes.ts';
import { CHECK_LABEL, type QualityCheck } from '../types.ts';

export function checkCaptionLength(input: { platform: string; caption: string }): QualityCheck {
  const { platform, caption } = input;
  const base = { id: 'caption_length' as const, label: CHECK_LABEL.caption_length };
  const name = platformDisplayName(platform);
  const spec = getPlatformSpec(platform.toLowerCase().trim());

  if (!spec) {
    return {
      ...base,
      status: 'skipped',
      detail: `Mora has no caption limits on record for ${name}.`,
    };
  }

  const len = caption.length;
  const max = spec.caption.maxChars;
  const [lo, hi] = spec.caption.sweetSpotChars;

  if (len === 0) {
    return { ...base, status: 'skipped', detail: 'No caption to check yet.' };
  }

  if (len > max) {
    return {
      ...base,
      status: 'fail',
      detail: `${name} cuts off after ${max.toLocaleString()} characters. This caption is ${len.toLocaleString()}.`,
      fix: trimCaptionToLimitFix(platform, caption, max),
    };
  }

  if (len > hi) {
    return {
      ...base,
      status: 'warn',
      detail: `Best ${name} captions run ${lo}–${hi} characters. This one is ${len.toLocaleString()}.`,
    };
  }

  // DELIBERATE: no warning for captions BELOW the sweet spot. LinkedIn's band
  // is [800, 1500] (platform-specs.ts:80), so a below-band warning would fire
  // on essentially every LinkedIn caption Mora writes, and a chip that cries
  // wolf is worse than no chip at all. Short is a taste call, not a defect.
  //
  // But the SENTENCE still has to be true. Caught grading real prod rows: a
  // below-band caption previously read "0 characters — inside Instagram's
  // 125–400 range", which is simply false. A short caption passes, and says
  // what is actually true about it — that it is under the hard limit.
  if (len >= lo) {
    return {
      ...base,
      status: 'pass',
      detail: `${len.toLocaleString()} characters — inside ${name}'s ${lo}–${hi} range.`,
    };
  }

  return {
    ...base,
    status: 'pass',
    detail: `${len.toLocaleString()} characters — well under ${name}'s ${max.toLocaleString()}-character limit.`,
  };
}

/**
 * Where each platform hides the rest of the caption behind "more".
 *
 * ⚠️ THESE ARE PRODUCT JUDGEMENT, NOT VENDOR-PUBLISHED NUMBERS. No platform
 * documents its truncation point; these are estimates in exactly the sense
 * `placement-matrix.ts:54` uses the word. Consequently this check is capped at
 * WARN severity forever and must never be able to block — and these numbers
 * must never be cited elsewhere as fact. A wrong Threads accept window sat in
 * placement-matrix.ts on the same kind of assumption until it was corrected on
 * 2026-07-25 (see its comment at :289-304).
 *
 * Platforms absent from this map are treated as "shows the whole caption".
 */
const FOLD_ESTIMATE: Partial<Record<PlatformId, number>> = {
  instagram: 125,
  facebook: 125,
  linkedin: 210,
  youtube: 100,
};

const PLATFORM_FOLD_ALIASES: Record<string, PlatformId> = { x: 'twitter' };

/** The hook: the shorter of the first line and the first sentence. */
export function captionHook(caption: string): string {
  const trimmed = caption.trim();
  if (!trimmed) return '';
  const firstLine = trimmed.split('\n')[0].trim();
  const firstSentence = trimmed.split(/(?<=[.!?…])\s/)[0].trim();
  const candidates = [firstLine, firstSentence].filter((value) => value.length > 0);
  if (candidates.length === 0) return trimmed;
  return candidates.reduce((shortest, value) =>
    value.length < shortest.length ? value : shortest,
  );
}

export function checkCaptionFold(input: { platform: string; caption: string }): QualityCheck {
  const { platform, caption } = input;
  const base = { id: 'caption_fold' as const, label: CHECK_LABEL.caption_fold };
  const name = platformDisplayName(platform);
  const key = platform.toLowerCase().trim();
  const platformId = (PLATFORM_FOLD_ALIASES[key] ?? key) as PlatformId;
  const fold = FOLD_ESTIMATE[platformId];

  if (fold === undefined) {
    return { ...base, status: 'skipped', detail: `${name} shows the whole caption.` };
  }

  const hook = captionHook(caption);
  if (hook.length === 0) {
    return { ...base, status: 'skipped', detail: 'No caption to check yet.' };
  }

  if (hook.length > fold) {
    // WARN, never FAIL — and no fix. Auto-truncating a hook destroys the
    // writer's meaning; a button that produced worse copy and called it fixed
    // would be exactly the fabricated success E6 forbids.
    return {
      ...base,
      status: 'warn',
      detail: `Your opening line is ${hook.length} characters. ${name} hides everything past ${fold} behind "more".`,
    };
  }

  return {
    ...base,
    status: 'pass',
    detail: `Your opening line is ${hook.length} characters — it survives ${name}'s ${fold}-character fold.`,
  };
}
